/**
 * ================================================================
 * API SERVICE: ProfileService
 * ================================================================
 *
 * @service          profileApi
 * @description      API service for managing user profile information
 * @baseUrl          /ap/get-ap-info
 *
 * @endpoints
 * - GET    /ap/get-ap-info - Fetch AP (Authorized Person) profile details
 *
 * @authentication  AuthToken header required for all endpoints
 * @rateLimit        Standard API rate limiting applies
 *
 * @caching
 * - Strategy: RTK Query automatic caching
 * - TTL: 10 minutes for profile data
 *
 * @errorHandling
 * - Global handler: transformApiError for consistent error management
 * - Auth errors: Automatic session clearing and login redirect
 * - Network errors: User-friendly error messages
 *
 * @types
 * - ApInfoDetailsResponse: Profile details structure (from @/types/profile)
 * - ApiError: Standardized error response format (from @/types/common)
 *
 * ================================================================
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiErrorTransformer, createBaseQuery } from '@/lib/api/baseQuery';
import type { ApInfoDetailsResponse } from '@/types/profile/ProfileDetails';

export const profileApi = createApi({
  reducerPath: 'profileApi',
  baseQuery: createBaseQuery(
    import.meta.env.VITE_API_BASE_URL || 'https://web.arhamshare.com/apb/v2',
  ),
  endpoints: (builder) => ({
    /**
     * Fetch AP (Authorized Person) profile details
     *
     * @endpoint GET /ap/get-ap-info
     * @returns {ApInfoDetailsResponse} Complete profile information
     * @cache Long-lived cache (10 minutes) for profile data
     */
    getProfileDetails: builder.query<ApInfoDetailsResponse, void>({
      query: () => ({
        url: '/ap/get-ap-info',
        method: 'GET',
      }),
      keepUnusedDataFor: 600, // Keep profile data for 10 minutes

      // ================================================================
      // RESPONSE TRANSFORMATION
      // ================================================================
      //
      // Purpose: Process successful API responses
      // Validation: Check for server-side error indicators
      // Error handling: Throw errors for failed responses
      // ================================================================

      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch profile details');
        }
        return response;
      },

      // ================================================================
      // ERROR TRANSFORMATION
      // ================================================================
      //
      // Purpose: Process API errors using global error handler
      // Benefits: Consistent error handling, auth redirects, user feedback
      // Implementation: Uses centralized transformApiError function
      // ================================================================

      transformErrorResponse: createApiErrorTransformer('ProfileDetails', true),
    }),
  }),
});

export const { useGetProfileDetailsQuery } = profileApi;

/**
 * ================================================================
 * END OF SERVICE: ProfileService
 * ================================================================
 *
 * @exports          profileApi, useGetProfileDetailsQuery hook
 * @dependencies     RTK Query, base query configuration, profile types
 * @usedBy           Profile components and pages
 *
 * @maintenance
 * - Cache strategy: Long TTL for relatively stable profile data
 * - Error handling: Standard global error processing
 * - Performance: Minimal API calls due to caching
 *
 * @security
 * - Authentication: Handled by base query configuration
 * - Data validation: Server response validation in transformResponse
 * - Error exposure: Safe error messages via global error handler
 *
 * ================================================================
 */
